import Button from 'components/ui/button';
import { Carga } from 'hooks/useCargaPorChave';
import { FaseDoCiclo } from 'pages/RealizacaoDestaque/realizacoes-do-semestre';
import { FormatNumber } from 'utils/helpers';

import { ColunaDaFase, colunasPorFase, Historicos, Programa, PROGRAMAS } from '../../pontos-por-fase';
import CabecalhoDaSecao from './CabecalhoDaSecao';

/** Nome e cor de cada programa na legenda e na barra (Meu Perfil v2, `PROGRAMAS`). */
export const LEGENDA: Record<Programa, { nome: string; cor: string }> = {
  reconhecimentos: { nome: 'Reconhecimentos', cor: 'bg-brand-pink-light' },
  realizacoes: { nome: 'Realização Destaque', cor: 'bg-brand-green' },
  ecoa: { nome: 'Ações Ecoa', cor: 'bg-brand-purple-light' },
  engajamento: { nome: 'Ações de Engajamento', cor: 'bg-brand-yellow' },
};

/** Altura da barra da fase de maior total; a menor barra (fase futura ou com 0) tem 8px. */
const ALTURA_MAXIMA = 168;
const ALTURA_MINIMA = 8;

function alturaDa(coluna: ColunaDaFase, maior: number): number {
  if (coluna.total === undefined || maior <= 0) return ALTURA_MINIMA;

  return Math.max(ALTURA_MINIMA, Math.round((coluna.total / maior) * ALTURA_MAXIMA));
}

function Coluna({ coluna, maior }: { coluna: ColunaDaFase; maior: number }) {
  const futura = coluna.total === undefined;
  // Sem ponto (fase futura ou com 0), a barra mínima de 8px fica cinza para aparecer.
  const vazia = !coluna.total;

  return (
    <li className="flex h-full min-w-0 flex-col items-center justify-end gap-2">
      <span className="font-display text-[15px] font-bold text-brand-blue">
        {futura ? '—' : FormatNumber(coluna.total, false)}
      </span>
      <div
        data-barra
        className={`flex w-full max-w-[110px] flex-col-reverse overflow-hidden rounded-t-lg ${vazia ? 'bg-surface-200' : ''}`}
        style={{ height: `${alturaDa(coluna, maior)}px` }}
      >
        {!vazia &&
          PROGRAMAS.filter(programa => coluna.pontos[programa] > 0).map(programa => (
            <div
              key={programa}
              data-programa={programa}
              title={`${LEGENDA[programa].nome}: ${FormatNumber(coluna.pontos[programa], false)} pontos`}
              className={`shrink-0 ${LEGENDA[programa].cor}`}
              style={{ height: `${((coluna.pontos[programa] / (coluna.total as number)) * 100).toFixed(2)}%` }}
            />
          ))}
      </div>
      <span className="text-[13px] font-bold text-content">{coluna.mes}</span>
      <span
        className={`-mt-[5px] whitespace-nowrap text-[11px] ${coluna.situacao === 'em-andamento' ? 'text-brand-blue' : 'text-content-soft'}`}
      >
        {coluna.rotuloDaSituacao}
      </span>
    </li>
  );
}

function Grafico({ colunas }: { colunas: ColunaDaFase[] }) {
  const maior = Math.max(0, ...colunas.map(coluna => coluna.total ?? 0));

  return (
    <>
      <ul aria-label="Legenda" className="m-0 flex list-none flex-wrap gap-5 p-0">
        {PROGRAMAS.map(programa => (
          <li key={programa} className="inline-flex items-center gap-[7px] text-xs text-content">
            <span aria-hidden="true" data-cor className={`h-2.5 w-2.5 shrink-0 rounded-[3px] ${LEGENDA[programa].cor}`} />
            {LEGENDA[programa].nome}
          </li>
        ))}
      </ul>
      <ul
        aria-label="Fases do ciclo"
        className="m-0 grid h-[240px] list-none items-end gap-3 p-0 lg:gap-[18px]"
        style={{ gridTemplateColumns: `repeat(${Math.max(colunas.length, 1)}, minmax(0, 1fr))` }}
      >
        {colunas.map(coluna => (
          <Coluna key={coluna.id} coluna={coluna} maior={maior} />
        ))}
      </ul>
    </>
  );
}

interface PontosPorFaseProps {
  carga: Carga<Historicos>;
  /** Fases do ciclo exibido (`cyclesExtended[].steps`). */
  fases?: FaseDoCiclo[];
  onTentarDeNovo: () => void;
  /** `ref` que liga a carga quando a seção entra na tela (AJ2-32). */
  observar: (elemento: Element | null) => void;
  /** Instante de referência da situação de cada fase; injetável para teste. */
  agora?: number;
}

/** AJ2-28 a AJ2-32: pontos dos quatro programas em cada mês do ciclo. */
export default function PontosPorFase({ carga, fases, onTentarDeNovo, observar, agora = Date.now() }: PontosPorFaseProps) {
  return (
    <section ref={observar} aria-labelledby="titulo-pontos-por-fase">
      <CabecalhoDaSecao
        id="titulo-pontos-por-fase"
        titulo="Pontos por fase"
        subtitulo="Pontos de todos os programas em cada mês do ciclo"
      />
      <div className="flex flex-col gap-[18px] rounded-2xl bg-surface-0 px-5 pb-5 pt-[22px] shadow-jj-01 lg:px-7">
        {carga.estado === 'carregando' && (
          <span
            role="status"
            aria-label="Carregando pontos por fase"
            className="block h-[240px] animate-pulse rounded-xl bg-surface-100"
          />
        )}
        {carga.estado === 'erro' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <p role="alert" className="m-0 text-sm text-content">
              Não foi possível carregar os pontos por fase.
            </p>
            <Button variant="secundario" size="sm" onClick={onTentarDeNovo}>
              Tentar de novo
            </Button>
          </div>
        )}
        {carga.estado === 'pronto' && <Grafico colunas={colunasPorFase(carga.dados, fases, agora)} />}
      </div>
    </section>
  );
}
